
import React, { useState } from "react";

const policies = [
  {
    id: "privacy",
    title: "Privacy Policy",
    subtitle: "How we collect, use and protect your information",
  },
  {
    id: "terms",
    title: "Terms & Conditions",
    subtitle: "Rules for using the BirdMyTrip platform",
  },
  {
    id: "refund",
    title: "Cancellation & Refund",
    subtitle: "Cancellation, rescheduling and refund guidelines",
  },
  {
    id: "cookies",
    title: "Cookie Policy",
    subtitle: "How cookies and similar technologies are used",
  },
];

function Section({ title, children }) {
  return (
    <section className="mb-7">
      <h2 className="mb-3 text-lg font-bold text-slate-900">{title}</h2>
      <div className="text-sm leading-7 text-slate-600">{children}</div>
    </section>
  );
}

export default function VendorPolicies() {
  const [active, setActive] = useState("privacy");

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 rounded-3xl bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-800 p-7 text-white sm:p-10">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-blue-200">
            BirdMyTrip · Vendor Resources
          </p>

          <h1 className="text-3xl font-bold sm:text-4xl">
            Privacy & Policies
          </h1>

          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
            Learn how BirdMyTrip handles information and review the policies
            that apply to your use of the platform.
          </p>

          <div className="mt-6 flex flex-wrap gap-3 text-xs text-slate-200">
            <span className="rounded-full border border-white/20 px-3 py-2">
              Vendor Information
            </span>
            <span className="rounded-full border border-white/20 px-3 py-2">
              Privacy & Security
            </span>
            <span className="rounded-full border border-white/20 px-3 py-2">
              Platform Policies
            </span>
          </div>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <p className="px-3 pb-3 pt-2 text-xs font-bold uppercase tracking-wider text-slate-400">
              Policy Documents
            </p>

            {policies.map((policy) => (
              <button
                key={policy.id}
                type="button"
                onClick={() => setActive(policy.id)}
                className={`mb-2 w-full rounded-xl p-4 text-left transition ${
                  active === policy.id
                    ? "bg-blue-700 text-white shadow-md"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="block text-sm font-bold">
                  {policy.title}
                </span>
                <span
                  className={`mt-1 block text-xs leading-5 ${
                    active === policy.id
                      ? "text-blue-100"
                      : "text-slate-500"
                  }`}
                >
                  {policy.subtitle}
                </span>
              </button>
            ))}
          </aside>

          <main className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8 lg:p-10">
            {active === "privacy" && (
              <>
                <div className="mb-8 border-b border-slate-100 pb-6">
                  <p className="mb-2 text-xs font-bold uppercase tracking-widest text-blue-700">
                    Legal Document
                  </p>
                  <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                    Privacy Policy
                  </h2>
                  <p className="mt-3 text-sm text-slate-500">
                    Last updated: September 2026
                  </p>
                </div>

                <p className="mb-7 text-sm leading-7 text-slate-600">
                  This Privacy Policy explains how BirdMyTrip, operated by
                  Birdella International Private Limited ("we", "us" or
                  "our"), collects, uses, stores and protects information
                  when vendors and other users access or use our platform.
                  Please read this policy carefully.
                </p>

                <Section title="1. Information We Collect">
                  We may collect information needed to create and manage
                  accounts, operate bookings and provide platform services.
                  This may include:
                  <ul className="mt-3 list-disc space-y-2 pl-5">
                    <li>
                      <strong>Account information:</strong> name, email
                      address, phone number and login-related information.
                    </li>
                    <li>
                      <strong>Business information:</strong> business name,
                      business address, property or service details, and
                      verification documents submitted by vendors.
                    </li>
                    <li>
                      <strong>Booking information:</strong> reservation
                      details, customer requests, booking status and
                      transaction references.
                    </li>
                    <li>
                      <strong>Payment information:</strong> payment status,
                      payout records and transaction identifiers. Payment
                      details may be processed by third-party payment
                      providers.
                    </li>
                    <li>
                      <strong>Technical information:</strong> device,
                      browser, usage, log and diagnostic information where
                      collected by our services.
                    </li>
                    <li>
                      <strong>Support communications:</strong> messages,
                      complaints and information you provide when contacting
                      support.
                    </li>
                  </ul>
                </Section>

                <Section title="2. How We Use Your Information">
                  We may use collected information to:
                  <ul className="mt-3 list-disc space-y-2 pl-5">
                    <li>Create, authenticate and manage accounts.</li>
                    <li>Review vendor applications and verification details.</li>
                    <li>Manage listings, availability and reservations.</li>
                    <li>Process transactions and manage vendor payouts.</li>
                    <li>Provide customer service and resolve disputes.</li>
                    <li>Maintain platform security and prevent fraud.</li>
                    <li>Improve functionality, reliability and user experience.</li>
                    <li>Meet applicable legal and regulatory obligations.</li>
                  </ul>
                </Section>

                <Section title="3. Sharing of Information">
                  We may share relevant information with service providers
                  that help operate the platform, including payment
                  processors, hosting providers, verification services and
                  technical support providers. Information may also be
                  disclosed where required by law, to protect legal rights,
                  or as part of a business restructuring where legally
                  permitted.
                  <p className="mt-3">
                    We do not promise that information is never shared.
                    Sharing is limited to appropriate purposes and applicable
                    legal requirements.
                  </p>
                </Section>

                <Section title="4. Data Security">
                  We use reasonable technical and organisational measures
                  designed to protect information against unauthorised
                  access, loss, misuse or alteration. However, no online
                  system or electronic transmission can be guaranteed to be
                  completely secure.
                </Section>

                <Section title="5. Data Retention">
                  We retain information for as long as reasonably necessary
                  to provide services, maintain business and transaction
                  records, resolve disputes, enforce agreements and comply
                  with applicable legal requirements. Retention periods may
                  vary depending on the type of information and its purpose.
                </Section>

                <Section title="6. Your Rights and Choices">
                  Subject to applicable law, you may request access to,
                  correction of or deletion of certain personal information.
                  You may also raise concerns about how your information is
                  handled. Some information may need to be retained where
                  legally required or necessary for legitimate operational
                  purposes.
                </Section>

                <Section title="7. Cookies and Similar Technologies">
                  Our website or services may use cookies and similar
                  technologies for functionality, security, preferences and
                  usage analysis. Available controls depend on your browser
                  and our platform settings. See the Cookie Policy for more
                  information.
                </Section>

                <Section title="8. Third-Party Services">
                  Our platform may link to or integrate with third-party
                  services. Those services may process information under
                  their own privacy policies and terms. We encourage you to
                  review those documents before using third-party services.
                </Section>

                <Section title="9. Changes to This Policy">
                  We may update this Privacy Policy when our services,
                  practices or legal obligations change. Where appropriate,
                  we will publish the revised policy on the platform and
                  update the revision date.
                </Section>

                <Section title="10. Contact Us">
                  For privacy-related questions or requests, contact
                  BirdMyTrip through the official support channels published
                  on our website. Add the verified company contact details
                  here before publishing this policy.
                </Section>

                <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                  <strong>Publication note:</strong> This is a starter
                  privacy policy for the vendor panel. Verify it against
                  Birdella International Private Limited's actual data
                  practices, retention periods, service providers and
                  official contact details before publishing it as the
                  final legal policy.
                </div>
              </>
            )}

            {active === "terms" && (
              <PolicyPlaceholder
                title="Terms & Conditions"
                text="Add the Terms and Conditions text supplied for BirdMyTrip here."
              />
            )}

            {active === "refund" && (
              <PolicyPlaceholder
                title="Cancellation & Refund Policy"
                text="Add the approved cancellation, rescheduling and refund policy here."
              />
            )}

            {active === "cookies" && (
              <PolicyPlaceholder
                title="Cookie Policy"
                text="Add the Cookie Policy text supplied for BirdMyTrip here."
              />
            )}

            <div className="mt-8 border-t border-slate-100 pt-5 text-xs text-slate-400">
              BirdMyTrip · Birdella International Private Limited
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function PolicyPlaceholder({ title, text }) {
  return (
    <div className="py-8">
      <p className="mb-2 text-xs font-bold uppercase tracking-widest text-blue-700">
        Legal Document
      </p>
      <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
      <p className="mt-4 text-sm leading-7 text-slate-600">{text}</p>
      <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        This section is a placeholder. Replace it with the complete approved
        policy text before publishing.
      </div>
    </div>
  );
}
